"use client";

import {
  isValidElement,
  type ReactElement,
  type ReactNode,
  type FC,
  type Ref,
} from "react";
import { isString, mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import { messageBodyStyle, contentStyle, messageStyle } from "./Message.css";
import { Card, type CardProps } from "../Card/Card";
import type { IconName } from "../../theme";
import { Icon, type IconProps } from "../Icon/Icon";
import type { ClassNameProp } from "../../props";
import { Body } from "../Body/Body";
import { Markdown } from "../Markdown/Markdown";
import { Flex } from "../Flex/Flex";

export type MessageProps = ClassNameProp &
  Omit<CardProps, "variant"> & {
    variant?: "default" | "success" | "error" | "warning";
    body?: ReactNode;
    children?: ReactNode;
    icon?: IconName | ReactElement<any>;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  } & RecipeVariants<typeof messageStyle>;

/**
 * A message component that displays a message with leading and trailing edges.
 */
export const Message: FC<MessageProps> = ({
  variant: variantProp = "default",
  body,
  alignSelf,
  children,
  className,
  icon,
  ref,
  ...props
}) => {
  const parsedIcon: IconName | ReactElement<any> | undefined = (() => {
    if (icon) {
      return icon;
    }

    switch (variantProp) {
      case "success":
        return "checkCircleFilled";
      case "error":
        return "alertFilled";
      case "warning":
        return "alertFilled";
      case "default":
        return "infoFilled";
      default:
        return undefined;
    }
  })();

  const iconColor: IconProps["color"] = (() => {
    switch (variantProp) {
      case "success":
        return "success";
      case "error":
        return "error";
      case "warning":
        return "warning";
      case "default":
        return "primary";
      default:
        return undefined;
    }
  })();

  const variant: CardProps["variant"] = (() => {
    switch (variantProp) {
      case "success":
        return "successSurface";
      case "error":
        return "errorSurface";
      case "warning":
        return "warningSurface";
      case "default":
        return "infoSurface";
    }
  })();

  return (
    <Card
      ref={ref}
      className={mergeClasses(messageStyle({ alignSelf }), className)}
      contentClassName={contentStyle}
      variant={variant}
      edgeDirection="horizontal"
      padding="s"
      borderRadius="xs"
      {...props}
    >
      {parsedIcon && isValidElement(parsedIcon) ? (
        parsedIcon
      ) : (
        <Icon icon={parsedIcon} color={iconColor} size="l" />
      )}
      <Flex direction="column" gap="s" align="flex-start" justify="flex-start">
        {body && (
          <Body className={messageBodyStyle}>
            {isString(body) ? <Markdown content={body} /> : body}
          </Body>
        )}
        {children}
      </Flex>
    </Card>
  );
};
